/**
 * 스프레드시트(.xlsx/.xls/.csv) 파싱 공용 유틸.
 *
 * 다중 문서 스프레드시트(`components/spreadsheet-docs/SpreadsheetDocuments.tsx`)와
 * 자재 일괄 등록(`materialsExcelImport.ts`)이 모두 사용한다.
 *
 *  - `parseExcelWorkbook(File)` : 업로드된 파일을 lazy xlsx 로 파싱 → 워크북 + 시트 이름.
 *  - `extractSheetAsGrid(workbook, sheetName)` : 특정 시트를 헤더+데이터 행 배열로 평탄화.
 *
 * 셀 값/병합/서식은 무시하고 텍스트 값만 추출한다 — 양쪽 모두 schema-light 한 사용 패턴.
 * xlsx 모듈은 lazy import 라 초기 번들에 영향이 없다.
 */

type XLSXModule = typeof import('xlsx');

let xlsxPromise: Promise<XLSXModule> | null = null;

function loadXLSX(): Promise<XLSXModule> {
  if (!xlsxPromise) {
    xlsxPromise = import('xlsx');
  }
  return xlsxPromise;
}

export interface ParsedWorkbook {
  workbook: import('xlsx').WorkBook;
  sheetNames: string[];
}

/**
 * CSV 바이트를 문자열로 디코딩한다.
 *
 * 한국어 CSV 는 인코딩이 갈린다 — "CSV UTF-8" 로 저장하면 UTF-8, 한국어 Excel 의
 * 기본 "CSV(쉼표로 분리)" 는 CP949 다. UTF-8 로 먼저 엄격 디코딩(fatal)해 보고,
 * 실패하면 CP949 로 떨어뜨린다. 순수 ASCII 는 어느 쪽이든 같으므로 안전하다.
 */
function decodeCsv(buffer: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('euc-kr').decode(buffer);
  }
}

/**
 * 업로드된 File 을 읽어 워크북 + 시트 목록을 반환.
 *
 * .csv 도 받는다 — xlsx 모듈이 CSV 를 시트 1개짜리 워크북으로 파싱해 주므로
 * 호출부(시트 선택 → 그리드 추출)는 엑셀과 완전히 동일하게 동작한다.
 */
export async function parseExcelWorkbook(file: File): Promise<ParsedWorkbook> {
  const XLSX = await loadXLSX();
  const buffer = await file.arrayBuffer();
  const isCsv = /\.csv$/i.test(file.name);
  // cellDates:false → 날짜 셀도 일관되게 raw 문자열로 처리하기 위해 직접 변환.
  const workbook = isCsv
    ? XLSX.read(decodeCsv(buffer), { type: 'string', cellDates: false })
    : XLSX.read(buffer, { type: 'array', cellDates: false });
  return { workbook, sheetNames: workbook.SheetNames };
}

export interface SheetGrid {
  /** 첫 행을 헤더로 본 컬럼 이름들. 빈 헤더는 '컬럼 N' 으로 채운다. */
  headers: string[];
  /** 헤더 이후의 데이터 행. 각 행은 컬럼 순서대로 정렬된 문자열 셀 값. */
  dataRows: string[][];
}

/** 셀 값을 표시용 문자열로 정규화. null/undefined → ''. 숫자/불리언은 String(). */
function cellToString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  // Date 객체가 들어올 수 있음(cellDates 옵션 위치에 따라). ISO 로 평탄화.
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
}

/**
 * 워크시트의 `!ref` 범위 안 셀을 직접 순회해 헤더 + 데이터 행 배열을 만든다.
 *  - 완전히 빈 행은 스킵.
 *  - 헤더가 비어있는 컬럼은 '컬럼 N' 으로 보강(중복 처리는 호출부에서).
 */
export function extractSheetAsGrid(
  workbook: import('xlsx').WorkBook,
  sheetName: string,
): SheetGrid {
  const ws = workbook.Sheets[sheetName];
  if (!ws) {
    return { headers: [], dataRows: [] };
  }
  return extractWorksheet(ws);
}

function extractWorksheet(ws: import('xlsx').WorkSheet): SheetGrid {
  const ref = ws['!ref'];
  if (!ref) {
    return { headers: [], dataRows: [] };
  }
  const [tl, br] = ref.split(':');
  const tlCol = colLetterToIndex(tl.replace(/\d+/g, ''));
  const tlRow = parseInt(tl.replace(/[^0-9]/g, ''), 10) - 1;
  const brCol = colLetterToIndex((br ?? tl).replace(/\d+/g, ''));
  const brRow = parseInt((br ?? tl).replace(/[^0-9]/g, ''), 10) - 1;

  const rows: string[][] = [];
  for (let r = tlRow; r <= brRow; r++) {
    const row: string[] = [];
    for (let c = tlCol; c <= brCol; c++) {
      const addr = `${colIndexToLetter(c)}${r + 1}`;
      const cell = ws[addr];
      row.push(cell ? cellToString(cell.v) : '');
    }
    rows.push(row);
  }
  if (rows.length === 0) return { headers: [], dataRows: [] };

  const rawHeaders = rows[0];
  const headers = rawHeaders.map((h, i) => (h && h.trim()) || `컬럼 ${i + 1}`);
  const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell !== ''));

  return { headers, dataRows };
}

function colLetterToIndex(letters: string): number {
  let n = 0;
  for (let i = 0; i < letters.length; i++) {
    n = n * 26 + (letters.charCodeAt(i) - 64);
  }
  return n - 1;
}

function colIndexToLetter(index: number): string {
  let n = index + 1;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}
